import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

/** Metadato del registro de asistencia (listado `detalle.documento_asistencia`). */
export interface DocumentoAsistenciaActividadGrupalApi {
  mime_type?: string | null;
  fecha_subida?: string | null;
  tamano_bytes?: number | null;
  nombre_archivo?: string | null;
  ruta_almacenamiento?: string | null;
  url_descarga?: string | null;
}

/** Nodo `detalle` del ítem en el listado de actividades grupales. */
export interface DetalleActividadGrupalApi {
  documento_asistencia?: DocumentoAsistenciaActividadGrupalApi | null;
  documento_evidencia?: DocumentoAsistenciaActividadGrupalApi | null;
  documento_otro?: DocumentoAsistenciaActividadGrupalApi | null;
  objetivo_otro?: string | null;
  hora_programada?: string | null;
}

/** Campos multipart del endpoint de asistencia/evidencia. */
export type CampoDocumentoAsistenciaGrupal =
  | 'documento_asistencia'
  | 'documento_evidencia'
  | 'documento_otro';

/** Actividad grupal (UI). Campos API conservan nomenclatura legacy `id_asesoria_especializada`. */
export interface ActividadGrupal {
  id_asesoria_especializada: number;
  /** Jerarquía / Unidad Operativa asociada (viene desde el endpoint actualizado). */
  id_jerarquia: number;
  nombre: string;
  asesor: string | null;
  categoria: string;
  fecha: string;
  fecha_inicio?: string;
  fecha_termino?: string;
  lugar: string | null;
  objetivos: string | null;
  estado: string | null;
  /** Si es `false`, no se deben ofrecer editar ni eliminar en UI. Ausente se trata como permitido (API antigua). */
  acciones?: boolean;
  categoria_descripcion: string;
  /** Adjuntos y metadatos extendidos del listado. */
  detalle?: DetalleActividadGrupalApi | null;
  unidad_operativa?: string;
  notificar_ejecutivos?: boolean;
  id_objetivo_visita?: number[];
  objetivo_otro?: string | null;
}

export interface ActividadGrupalCreate {
  id_jerarquia: number;
  id_categoria_asesoria_especializada: number;
  nombre: string;
  objetivos: string;
  asesor: string;
  lugar: string;
  fecha: string; // date-time ISO
  fecha_inicio: string; // date-time ISO
  notificar_ejecutivos?: boolean;
  id_objetivo_visita?: number[];
  objetivo_otro?: string | null;
  hora_programada?: string | null;
}

export interface ActividadGrupalUpdate {
  id_jerarquia?: number | null;
  id_categoria_asesoria_especializada?: number | null;
  nombre?: string | null;
  objetivos?: string | null;
  asesor?: string | null;
  lugar?: string | null;
  fecha?: string | null; // date-time ISO
  fecha_inicio?: string | null; // date-time ISO
  notificar_ejecutivos?: boolean | null;
  id_objetivo_visita?: number[] | null;
  objetivo_otro?: string | null;
  hora_programada?: string | null;
}

export interface ActividadesGrupalesPaginadasResponse {
  items: ActividadGrupal[];
  total: number;
  page: number;
  size: number;
  pages: number;
}

export interface ActividadesGrupalesQueryParams {
  desde?: string;
  hasta?: string;
  sort?: string;
  order?: 'asc' | 'desc';
  search?: string;
  page?: number;
  size?: number;
}

export interface CategoriaActividadGrupalResponse {
  id_categoria_asesoria_especializada: number;
  descripcion: string;
}

@Injectable({
  providedIn: 'root'
})
export class ActividadGrupalService {
  private apiUrl = environment.apiUrl;
  private readonly http = inject(HttpClient);

  obtenerActividadesPorJerarquia(
    idPerfil: number,
    idJerarquia: number,
    queryParams: ActividadesGrupalesQueryParams = {}
  ): Observable<ActividadesGrupalesPaginadasResponse> {
    let params = new HttpParams();

    if (queryParams.desde) params = params.set('desde', queryParams.desde);
    if (queryParams.hasta) params = params.set('hasta', queryParams.hasta);
    if (queryParams.sort) params = params.set('sort', queryParams.sort);
    if (queryParams.order) params = params.set('order', queryParams.order);
    if (queryParams.search) params = params.set('search', queryParams.search);
    if (queryParams.page != null) params = params.set('page', queryParams.page.toString());
    if (queryParams.size != null) params = params.set('size', queryParams.size.toString());

    return this.http.get<ActividadesGrupalesPaginadasResponse>(
      `${this.apiUrl}/asesoriaEspecializada/perfil/${idPerfil}/jerarquia/${idJerarquia}`,
      { params }
    );
  }

  /**
   * Obtiene las categorías para el formulario de actividad grupal.
   * OpenAPI: GET /api/v1/asesoriaEspecializada/categorias
   */
  obtenerCategoriasActividadGrupal(): Observable<CategoriaActividadGrupalResponse[]> {
    return this.http.get<CategoriaActividadGrupalResponse[]>(
      `${this.apiUrl}/asesoriaEspecializada/categorias`
    ).pipe(
      catchError(error => {
        console.error('Error al obtener categorías de actividad grupal:', error);
        return of([]);
      })
    );
  }

  /**
   * Crear nueva actividad grupal.
   * OpenAPI: POST /api/v1/asesoriaEspecializada/
   */
  crearActividadGrupal(
    body: ActividadGrupalCreate
  ): Observable<ActividadGrupal> {
    return this.http.post<ActividadGrupal>(
      `${this.apiUrl}/asesoriaEspecializada/`,
      body
    );
  }

  /**
   * Editar actividad grupal.
   * OpenAPI: POST /api/v1/asesoriaEspecializada/{id_asesoria_especializada}
   */
  editarActividadGrupal(
    idAsesoriaEspecializada: number,
    body: ActividadGrupalUpdate
  ): Observable<ActividadGrupal> {
    return this.http.post<ActividadGrupal>(
      `${this.apiUrl}/asesoriaEspecializada/${idAsesoriaEspecializada}`,
      body
    );
  }

  /**
   * Eliminar actividad grupal.
   * OpenAPI: DELETE /api/v1/asesoriaEspecializada/{id_asesoria_especializada}
   */
  eliminarActividadGrupal(idAsesoriaEspecializada: number): Observable<void> {
    return this.http.delete<void>(
      `${this.apiUrl}/asesoriaEspecializada/${idAsesoriaEspecializada}`
    );
  }

  /**
   * Sube un documento nuevo de asistencia/evidencia de una actividad grupal.
   * OpenAPI: PUT /api/v1/asesoriaEspecializada/asistenciaAsesoria?id_asesoria_especializada={id}
   * Body: un solo campo multipart (`documento_asistencia`, `documento_evidencia` o `documento_otro`).
   * Solo el archivo nuevo de este slot; no reenviar otros documentos ya registrados.
   */
  subirAsistenciaActividadGrupal(
    idAsesoriaEspecializada: number,
    campo: CampoDocumentoAsistenciaGrupal,
    archivo: File
  ): Observable<boolean> {
    const form = new FormData();
    form.append(campo, archivo, archivo.name);
    const params = new HttpParams().set(
      'id_asesoria_especializada',
      String(idAsesoriaEspecializada)
    );
    return this.http.put<boolean>(
      `${this.apiUrl}/asesoriaEspecializada/asistenciaAsesoria`,
      form,
      { params }
    );
  }

  /**
   * Elimina un documento de asistencia/evidencia de una actividad grupal.
   * OpenAPI: PUT /api/v1/asesoriaEspecializada/eliminarAsistenciaAsesoria
   * Query: id_asesoria_especializada, nombre_documento
   */
  eliminarAsistenciaActividadGrupal(
    idAsesoriaEspecializada: number,
    nombreDocumento: CampoDocumentoAsistenciaGrupal
  ): Observable<boolean> {
    const params = new HttpParams()
      .set('id_asesoria_especializada', String(idAsesoriaEspecializada))
      .set('nombre_documento', nombreDocumento);
    return this.http.put<boolean>(
      `${this.apiUrl}/asesoriaEspecializada/eliminarAsistenciaAsesoria`,
      null,
      { params }
    );
  }
}
